import { useMemo } from 'react'
import { SearchSelect } from '../../kit'

/*
 * TagPicker — searchable, multi-select tag field.
 *
 * Shared by the Ticket filter bar and TicketDrawer so both behave identically.
 * Xano's `tag` is a string[] on the row AND a string[] query param, so multi-select
 * is native on both the read and write side.
 *
 * Now a thin wrapper over the kit's <SearchSelect multiple> (portalled menu, Esc
 * closes the menu only — never the Drawer underneath). It keeps the string[] API:
 * the kit hands back a Set, this turns it back into an array in the order the user
 * picked the tags (already-picked tags keep their place; new ones are appended).
 *
 * Props:
 *   value: string[]            selected tags
 *   onChange(next: string[])
 *   catalog: string[]          options (from GET /ticket/tag)
 *   placeholder: string
 *   variant: 'filter' | 'form' kept for callers; the kit field is one size
 *   disabled: boolean
 */
export default function TagPicker({ value = [], onChange, catalog = [], placeholder = 'เลือกแท็ก…', disabled = false }) {
  const list = Array.isArray(value) ? value : []
  // A selected tag that is not (yet) in the catalog stays pickable, so it is never
  // silently dropped on the next toggle.
  const options = useMemo(
    () => [...new Set([...catalog, ...list])].filter(Boolean).map((t) => ({ value: t, label: t })),
    [catalog, list.join('|')], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const change = (set) => {
    const picked = new Set([...set].map(String))
    const kept = list.filter((t) => picked.has(String(t)))
    const added = [...set].filter((t) => !list.some((x) => String(x) === String(t)))
    onChange?.([...kept, ...added])
  }

  return (
    <SearchSelect
      multiple
      values={list}
      onChange={change}
      options={options}
      placeholder={placeholder}
      searchPlaceholder="ค้นหาแท็ก…"
      emptyText={catalog.length ? 'ไม่พบแท็กที่ค้นหา' : 'ไม่มีแท็ก'}
      disabled={disabled}
    />
  )
}
